
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Briefcase,
  Database,
  Building,
  Users
} from 'lucide-react';

interface IntegrationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const IntegrationsDialog = ({ open, onOpenChange }: IntegrationsDialogProps) => {
  const handleConnectService = (service: string) => {
    toast.success(`Connected to ${service}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Data Sources & Integrations</DialogTitle>
          <DialogDescription>Connect your external systems and data sources</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <div className="p-4 flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex items-center">
                  <Briefcase className="h-5 w-5 mr-2" />
                  <h3 className="text-lg font-medium">ERP Systems</h3>
                </div>
                <Badge variant="outline">Integration</Badge>
              </div>
              <div className="p-4 pt-0">
                <p className="text-sm text-muted-foreground mb-4">Connect your ERP systems to sync financial data</p>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/6d/SAP_2011_logo.svg/1200px-SAP_2011_logo.svg.png" alt="SAP" className="h-6 w-12 mr-2 object-contain" />
                      <span>SAP</span>
                    </div>
                    <Button size="sm" onClick={() => handleConnectService("SAP")}>Connect</Button>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img src="https://www.oracle.com/a/ocom/img/cb71-oracle-logo.png" alt="Oracle" className="h-6 w-12 mr-2 object-contain" />
                      <span>Oracle ERP</span>
                    </div>
                    <Button size="sm" onClick={() => handleConnectService("Oracle ERP")}>Connect</Button>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/Microsoft_logo.svg/1024px-Microsoft_logo.svg.png" alt="Microsoft" className="h-6 w-12 mr-2 object-contain" />
                      <span>Microsoft Dynamics</span>
                    </div>
                    <Button size="sm" onClick={() => handleConnectService("Microsoft Dynamics")}>Connect</Button>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img src="https://tallysolutions.com/web17/assets/media/images/tally-logo.svg" alt="Tally" className="h-6 w-12 mr-2 object-contain" />
                      <span>Tally</span>
                    </div>
                    <Button size="sm" onClick={() => handleConnectService("Tally")}>Connect</Button>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-4 flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex items-center">
                  <Database className="h-5 w-5 mr-2" />
                  <h3 className="text-lg font-medium">Database Services</h3>
                </div>
                <Badge variant="outline">Integration</Badge>
              </div>
              <div className="p-4 pt-0">
                <p className="text-sm text-muted-foreground mb-4">Connect to external database services</p>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img src="https://www.microsoft.com/en-us/sql-server/img/sql-server-logo.svg" alt="SQL Server" className="h-6 w-12 mr-2 object-contain" />
                      <span>SQL Server</span>
                    </div>
                    <Button size="sm" onClick={() => handleConnectService("SQL Server")}>Connect</Button>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img src="https://www.mongodb.com/assets/images/global/MongoDB_Logo_FullColorBlack_RGB-4td3yuxzjs.svg" alt="MongoDB" className="h-6 w-12 mr-2 object-contain" />
                      <span>MongoDB</span>
                    </div>
                    <Button size="sm" onClick={() => handleConnectService("MongoDB")}>Connect</Button>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img src="https://www.postgresql.org/media/img/about/press/elephant.png" alt="PostgreSQL" className="h-6 w-8 mr-2 object-contain" />
                      <span>PostgreSQL</span>
                    </div>
                    <Button size="sm" onClick={() => handleConnectService("PostgreSQL")}>Connect</Button>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-4 flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex items-center">
                  <Building className="h-5 w-5 mr-2" />
                  <h3 className="text-lg font-medium">Banking Partners</h3>
                </div>
                <Badge variant="outline">Integration</Badge>
              </div>
              <div className="p-4 pt-0">
                <p className="text-sm text-muted-foreground mb-4">Connect to banking services</p>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img src="https://www.hdfcbank.com/content/api/contentstream-id/723fb80a-2dde-42a3-9793-7ae1be57c87f/e97b651e-8000-4718-b0c4-0f5b792fb536/common/logo.png" alt="HDFC" className="h-6 w-12 mr-2 object-contain" />
                      <span>HDFC Bank API</span>
                    </div>
                    <Button size="sm" onClick={() => handleConnectService("HDFC Bank API")}>Connect</Button>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img src="https://www.icicibank.com/content/dam/icicibank/icici-assets/logos/ilogo.png" alt="ICICI" className="h-6 w-8 mr-2 object-contain" />
                      <span>ICICI Corporate Connect</span>
                    </div>
                    <Button size="sm" onClick={() => handleConnectService("ICICI Corporate Connect")}>Connect</Button>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img src="https://www.onlinesbi.sbi/sbijava/images/logoNew.png" alt="SBI" className="h-6 w-12 mr-2 object-contain" />
                      <span>SBI YONO Business</span>
                    </div>
                    <Button size="sm" onClick={() => handleConnectService("SBI YONO Business")}>Connect</Button>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-4 flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  <h3 className="text-lg font-medium">HRMS</h3>
                </div>
                <Badge variant="outline">Integration</Badge>
              </div>
              <div className="p-4 pt-0">
                <p className="text-sm text-muted-foreground mb-4">Connect your HR Management Systems</p>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img src="https://workday-static.okta.com/resource/1670948492000/logo/workdaylogo" alt="Workday" className="h-6 w-12 mr-2 object-contain" />
                      <span>Workday</span>
                    </div>
                    <Button size="sm" onClick={() => handleConnectService("Workday")}>Connect</Button>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img src="https://www.bamboohr.com/blog/wp-content/uploads/2023/01/Bamboo_HR_logo.svg" alt="BambooHR" className="h-6 w-12 mr-2 object-contain" />
                      <span>BambooHR</span>
                    </div>
                    <Button size="sm" onClick={() => handleConnectService("BambooHR")}>Connect</Button>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img src="https://www.adp.com/-/media/adp/redesign2018/images/shared/adp-logo-110h-rgb.svg" alt="ADP" className="h-6 w-12 mr-2 object-contain" />
                      <span>ADP</span>
                    </div>
                    <Button size="sm" onClick={() => handleConnectService("ADP")}>Connect</Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IntegrationsDialog;
